import React, { useState, useEffect } from 'react';
import Form from 'screens/shared/form';
import Icon from 'screens/shared/icon';
import CardBasic from 'screens/shared/card-basic';
import { Paper, Collapse } from '@mui/material';
import {
  VALIDATION, AVAILABILITY_ZONE, STORAGE_TYPE, RESOURCE_TYPE, AVAILABILITY_ZONE$,
} from 'constant';
import { _t, objectToOptions, enumToArray } from 'utils';
import { useSelector } from 'react-redux';
import { actions, ACTIONS_TYPES } from 'redux/local';
import { CLOUD_INIT } from 'constant/cloud-init';
import { EditStorage } from '../edit/storage';

export default ({
  onAdd,
  items: storages = [],
  onEdit,
  selectToEdit,
  selectToDelete,
  deselectToEdit,
  isLimitExceeded,
}) => {
  const initStorage = {
    name: '',
    availabilityZone: AVAILABILITY_ZONE.AUTO,
    size: 10,
    type: STORAGE_TYPE.SSD,
    image: '',
    imagePassword: '',
    sshKey: '',
  };
  const [storage, setStorage] = useState(initStorage);
  const [openCreateForm, setOpenCreateForm] = useState(false);
  const [selectedImage, setSelectedImage] = useState({});
  const { [ACTIONS_TYPES.BACKUP_UNIT.GET_ALL]: backupUnits = [] } = useSelector((e) => e);
  useEffect(() => {
    actions.backupUnits.getAll();
  }, []);

  const storagesFields = [
    {
      name: 'name',
      type: 'text',
      title: _t('Name'),
    },
    {
      name: 'availabilityZone',
      type: 'dropdown',
      title: _t('Availability Zone'),
      options: [...enumToArray(AVAILABILITY_ZONE).map((e) => [e, AVAILABILITY_ZONE$[e]])],
    },
    {
      name: 'type',
      type: 'dropdown',
      title: _t('Type'),
      options: [...objectToOptions(STORAGE_TYPE, [STORAGE_TYPE.DAS])],
    },
    {
      name: 'size',
      title: _t('Size'),
      type: 'slider',
      range: [20, 600],
      showValue: true,
    },
    {
      name: 'image',
      type: 'hdd',
      onChange: (value) => setSelectedImage(value),
    },
    {
      name: 'imagePassword',
      type: 'password',
      title: 'Image Password',
      visible: false,
    },
    {
      name: 'sshKey',
      type: 'textarea',
      title: _t('SSH Key'),
      visible: false,
    },
    {
      name: 'userData',
      type: 'user-data',
      title: 'User Data',
      visible: false,
    },
    {
      name: 'backupunitId',
      type: 'dropdown',
      title: 'Backup Unit',
      visible: false,
    },
  ];

  const getChangeFields = (image) => [
    {
      name: 'imagePassword',
      props: {
        visible: !!image.licenceType && image.public,
      },
    },
    {
      name: 'backupunitId',
      props: {
        visible: !!image.licenceType && image.type !== RESOURCE_TYPE.SNAPSHOT,
        options: [...backupUnits.map(({ id: _id, properties }) => [
          _id,
          <span>
            {properties.name}
            <small>{` (${properties.email})`}</small>
          </span>,
        ])],
      },
    },
    {
      name: 'sshKey',
      props: {
        visible: image.licenceType ? (image.licenceType === 'LINUX' && image.public) : false,
      },
    },
    {
      name: 'userData',
      props: {
        visible: image.cloudInit === CLOUD_INIT.V1,
      },
    },
  ];

  const validations = [
    {
      name: 'name',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'imagePassword',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'imagePassword',
      type: VALIDATION.STRING,
      min: 5,
    },
    {
      name: 'sshKey',
      type: VALIDATION.REQUIRED,
    },
  ];

  const onSave = ({
    id = null, sshKey, backupunitId, ...item
  }) => {
    item.sshKeys = [sshKey];

    if (backupunitId) {
      item.backupunitId = backupunitId;
    }

    if (id) {
      return onEdit({ ...item, id });
    }

    onAdd(item);
    return setOpenCreateForm(false);
  };

  const addClickHandler = () => {
    setStorage(initStorage);
    setSelectedImage({});
    setOpenCreateForm((s) => !s);
  };

  return (
    <div className="add-box">
      <h3 className="screen-create-server-title">
        {_t('Storages')}
        {' '}
        {isLimitExceeded && (
          <span className="circle-button" onClick={addClickHandler}>
            <Icon name="plus" />
            {_t('Add')}
          </span>
        )}
      </h3>
      <Collapse in={openCreateForm} collapsedSize="0px">
        <Paper style={{ marginTop: 20 }}>
          {openCreateForm && (
            <Form
              fields={storagesFields}
              fieldsValues={storage}
              onSubmit={onSave}
              onCancel={() => setOpenCreateForm(false)}
              validations={validations}
              changeFields={getChangeFields(selectedImage)}
              onChange={(value) => {
                setStorage(value);
              }}
            />
          )}
        </Paper>
      </Collapse>
      <div className="cards cards-create-storage">
        {storages.map((volume) => {
          const {
            id, type, name, size, isEdit = false, image = {},
          } = volume;
          const { name: imageName = '' } = image;

          const { sshKeys: [sshKey = ''] = [], ...editValue } = volume;
          editValue.sshKey = sshKey;

          return (
            <div key={id}>
              <Collapse in={isEdit} collapsedSize="0px">
                <Paper style={{ marginTop: 20 }}>
                  {isEdit && (
                    <EditStorage
                      id={id}
                      storagesFields={storagesFields}
                      editValue={editValue}
                      type={type}
                      deselectToEdit={deselectToEdit}
                      getChangeFields={getChangeFields}
                      onSave={onSave}
                      validations={validations}
                    />
                  )}
                </Paper>
              </Collapse>
              {!isEdit && (
                <CardBasic
                  icon={type === STORAGE_TYPE.SSD ? 'sd-card' : type === STORAGE_TYPE.DAS ? 'memory' : 'hdd'}
                  details={(
                    <div className="props">
                      <span>{`${size} GB`}</span>
                      <span>{imageName}</span>
                    </div>
                  )}
                  iconText={type}
                  title={name}
                  actionButtons={type === STORAGE_TYPE.DAS ? {
                    edit: () => {
                      selectToEdit(id);
                    },
                  } : {
                    edit: () => {
                      selectToEdit(id);
                    },
                    delete: () => selectToDelete(id),
                  }}
                />
              )}
            </div>
          );
        }).reverse()}
      </div>
    </div>
  );
};
